import { Form } from 'informed';
import { map, prop, uniq } from 'ramda';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileSelectDate } from '@/components/form/ProfileDateSelect';
import { ProfileSelect } from '@/components/form/Select';
import {
  currencyFlags,
  mapCurrencyName,
  mapCurrencySymbol,
} from '@/helpers/currency';
import { useGetCommonStore } from '@/store';
import { getBonusStatusNameTranslation } from '../../../../helpers';

import { ScBonusesFiltration } from './styled';

interface BonusesFiltrationProps {
  setCurrency: (value: string) => void;
  setStatus: (value: string) => void;
  setDate: (value: [Date | null, Date | null]) => void;
}

export const BonusesFiltration: FC<BonusesFiltrationProps> = ({
  setCurrency,
  setDate,
  setStatus,
}) => {
  const { t, i18n } = useTranslation('bonuses');
  const useCommonStore = useGetCommonStore();

  const bonuses = useCommonStore.use.bonuses();
  const freespins = useCommonStore.use.freespins();

  const currencyOptions = useMemo(() => {
    const userBonusStages = uniq(
      map(prop('currency'), [...bonuses, ...freespins])
    );

    return userBonusStages.map((code) => {
      const mask = mapCurrencySymbol[code];
      const name = mapCurrencyName[code];

      const currencySymbol = mask ? mask.replace('{}', '') : code;

      return {
        name: `${currencySymbol}${name ? ` | ${name}` : ''}`,
        value: code,
        icon: currencyFlags[code],
      };
    });
  }, [bonuses, freespins]);

  const stagesSelectOptions = useMemo(() => {
    const userBonusStages = uniq(
      map(prop('stage'), [...bonuses, ...freespins])
    );

    return userBonusStages.map((stage) => {
      const statusTranslation = getBonusStatusNameTranslation(
        t,
        i18n,
        stage,
        true
      );

      return {
        name: statusTranslation,
        value: stage,
      };
    });
  }, [bonuses, freespins, i18n, t]);

  return (
    <ScBonusesFiltration>
      <Form>
        <div>
          <ProfileSelect
            canReset
            name="currency"
            onSelectOption={(value) => setCurrency(value.toString())}
            options={currencyOptions}
            placeholder={t('currency')}
          />
          <ProfileSelect
            canReset
            name="status"
            onSelectOption={(value) => setStatus(value.toString())}
            options={stagesSelectOptions}
            placeholder={t('status')}
          />
          <ProfileSelectDate
            canReset
            name="date"
            onSelectDateRange={(value) => setDate(value)}
          />
        </div>
      </Form>
    </ScBonusesFiltration>
  );
};
